import {
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	Collapse,
	IconButton,
	ListItemText,
	makeStyles,
	MenuItem,
	TextField,
	Typography,
	Button
} from '@material-ui/core'
import { ExpandMoreOutlined } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import { clone, cloneDeep } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useConstants from '../../../hooks/useConstants'
import DogBeef from '../../Dialogs/Pets/DogBeef'
import { purple, gray, dangers, errors } from '../../../utils/colors'

import { DeletableChip } from '../../Misc/DeletableChip'

const useStyles = makeStyles((theme) => ({
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0
		}
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		'&:last-child': {
			marginBottom: 0
		}
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	gridTriple: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '274px !important'
			},
			'& .MuiFormControl-root': {
				minWidth: '272px !important'
			}
		}
	},
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: '6px',
		backgroundColor: 'transparent !important'
	},
	footerButton: {
		fontFamily: 'Nunito',
		fontWeight: '800',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	},
	rowThing: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		fontSize: 14
	}
}))

const PetBehaviorCard = (props) => {
	const classes = useStyles()
	const { tags } = useConstants()
	const facility = useSelector((state) => state.specific.facilities.selected)
	let playgroup_types = facility ? facility.playgroup_types : []
	let { pet, setPet } = props

	// const sizes = ['Extra Small', 'Small', 'Medium', 'Large', 'Extra Large']
	// const ages = ['Puppy', 'Young', 'Adult', 'Senior']
	const playStyles = ['high-contact', 'low-contact', 'fast', 'puppy play']
	const energyLevels = ['low', 'medium', 'high']
	const [isExpanded, setIsExpanded] = useState(false)
	const [petSensitivityTags, setPetSensitivityTags] = useState([])
	const [petBehaviorTags, setPetBehaviorTags] = useState([])
	const [showOnlyFits, setShowOnlyFits] = useState(true)
	const [showOnlyFitsAlt, setShowOnlyFitsAlt] = useState(true)
	const [showOnlyFitsOther, setShowOnlyFitsOther] = useState(true)
	const [otherPlaygroupTypes, setOtherPlaygroupTypes] = useState([])
	// const [bestFitSelectOpen, setBestFitSelectOpen] = useState(false)
	const [dogBeef, openDogBeef] = useState(false)
	// const bestFitRef = React.useRef()

	useEffect(() => {
		if (playgroup_types && pet.other_playgroup_types) {
			let types = pet.other_playgroup_types.map((id) => playgroup_types.find((t) => t._id === id))
			setOtherPlaygroupTypes(types)
		}

		if (tags && pet.tags && pet.tags.sensitivity && pet.tags.sensitivity.length) {
			let sensitivityTags = tags.sensitivity.filter((tag) => {
				return pet.tags.sensitivity.findIndex((pt) => pt._id === tag._id) !== -1
			})

			setPetSensitivityTags(sensitivityTags)
		} else {
			setPetSensitivityTags([])
		}

		if (tags && pet.tags && pet.tags.behavior && pet.tags.behavior.length) {
			let behaviorTags = tags.behavior.filter((tag) => {
				return pet.tags.behavior.findIndex((pt) => pt._id === tag._id) !== -1
			})

			setPetBehaviorTags(behaviorTags)
		} else {
			setPetBehaviorTags([])
		}
	}, [tags, pet])

	useEffect(() => {
		if (pet.best_fit_playgroup_type && playgroup_types?.length) {
			let pg = getBestPlaygroups()
			if (!pg.includes(pet.best_fit_playgroup_type)) {
				setShowOnlyFits(false)
			}
		}
	}, [pet, playgroup_types])

	useEffect(() => {
		if (pet.alt_playgroup_type && playgroup_types?.length) {
			let pg = getAltPlaygroups()
			if (!pg.includes(pet.alt_playgroup_type)) {
				setShowOnlyFitsAlt(false)
			}
		}
	}, [pet, playgroup_types])

	const updatePet = (field, value) => {
		setPet({ ...pet, [field]: value })
	}

	const updateTags = (type, value) => {
		let updatedTags = cloneDeep(pet.tags)

		updatedTags[type] = value

		updatePet('tags', updatedTags)
	}

	const updateBestFitPlaygroup = (_id) => {
		updatePet('best_fit_playgroup_type', _id)
	}

	const updateOtherPlaygroups = (groups) => {
		setOtherPlaygroupTypes(groups)

		let ids = groups.filter(Boolean).map((g) => g._id)

		updatePet('other_playgroup_types', ids)
	}

	const removeTag = (type, value) => {
		let updatedTags = cloneDeep(pet.tags)
		let tagIndex = updatedTags[type].findIndex((t) => t._id === value._id)

		if (tagIndex !== -1) {
			updatedTags[type].splice(tagIndex, 1)
		}

		updatePet('tags', updatedTags)
	}

	const addBeefs = (newBeefs) => {
		const formerBeefs = pet.beefs || []
		const beefs = [...formerBeefs, ...newBeefs]
		updatePet('beefs', beefs)
	}

	const updateBeefs = (newBeefs) => {
		updatePet('beefs', newBeefs)
	}

	const getPetSize = () => {
		if (pet.height) return pet.height.toLowerCase()
		if (pet.weight <= 12) {
			return 'xs'
		} else if (pet.weight >= 13 && pet.weight < 31) {
			return 's'
		} else if (pet.weight >= 31 && pet.weight < 48) {
			return 'm'
		} else if (pet.weight >= 48 && pet.weight < 71) {
			return 'l'
		} else if (pet.weight >= 71) {
			return 'xl'
		} else {
			return null
		}
	}

	const getBestPlaygroups = () => {
		const size = getPetSize()
		const play_style = pet.play_style
		return playgroup_types
			.filter((type) => {
				// return group types that match
				if (size && play_style) return type.best_fit_sizes.includes(size) && type.play_style === play_style
				if (size) return type.best_fit_sizes.includes(size)
				if (play_style) return type.play_style === play_style
				return true
			})
			.map((type) => type._id)
	}

	const getAltPlaygroups = () => {
		const size = getPetSize()
		const play_style = pet.play_style
		return playgroup_types
			.filter((type) => {
				// return group types that match
				if (size && play_style) return type.best_fit_sizes.includes(size) && type.play_style === play_style
				if (size) return type.best_fit_sizes.includes(size)
				if (play_style) return type.play_style === play_style
				return true
			})
			.map((type) => type._id)
	}

	const getBestFits = () => {
		const size = getPetSize()
		const play_style = pet.play_style
		return playgroup_types
			.filter((type) => {
				if (!showOnlyFits) return true
				// return group types that match
				if (type._id === pet.best_fit_playgroup_type) return true
				if (size && play_style) return type.best_fit_sizes.includes(size) && type.play_style === play_style
				if (size) return type.best_fit_sizes.includes(size)
				if (play_style) return type.play_style === play_style
				return true
			})
			.filter((type) => {
				if (type._id === pet.best_fit_playgroup_type) return true // return option if previously selected
				if (pet.alt_playgroup_type === type._id) return false
				if (pet.other_playgroup_types?.includes(type._id)) return false
				return true
			})
	}

	const getAltFits = () => {
		const size = getPetSize()
		const play_style = pet.play_style
		return playgroup_types
			.filter((type) => {
				if (!showOnlyFitsAlt) return true
				if (type._id === pet.alt_playgroup_type) return true
				// return group types that match
				if (size && play_style) return type.best_fit_sizes.includes(size) && type.play_style === play_style
				if (size) return type.best_fit_sizes.includes(size)
				if (play_style) return type.play_style === play_style
				return true
			})
			.filter((type) => {
				if (type._id === pet.best_fit_playgroup_type) return false
				if (type._id === pet.alt_playgroup_type) return true
				if (pet.other_playgroup_types?.includes(type._id)) return false
				return true
			})
	}

	const getOtherFits = () => {
		const size = getPetSize()

		return playgroup_types
			.filter((type) => {
				if (!showOnlyFitsOther) return true
				// return group types that match
				if (size) return type.best_fit_sizes.includes(size)
				return true
			})
			.filter((type) => {
				if (type._id === pet.best_fit_playgroup_type || type._id === pet.alt_playgroup_type) return false
				return true
			})
	}

	return (
		<Card>
			<CardHeader
				title="Behavior"
				className={classes.header}
				onClick={() => setIsExpanded(!isExpanded)}
				action={
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: isExpanded
						})}
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						aria-label="show more">
						<ExpandMoreOutlined style={{ fill: '#777679' }} />
					</IconButton>
				}
			/>
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<CardContent>
					<div className={clsx([classes.grid, classes.gridDouble])}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								select
								id="play-style"
								value={pet.play_style}
								style={{ textTransform: 'capitalize' }}
								onChange={(e) => updatePet('play_style', e.target.value)}
								disabled={!pet.active}
								variant="filled"
								label="Play Style">
								{playStyles.map((playStyle) => (
									<MenuItem
										style={{ textTransform: 'capitalize' }}
										key={playStyle}
										value={playStyle}>
										{playStyle}
									</MenuItem>
								))}
							</TextField>
						</div>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								select
								id="energy-level"
								value={pet.energy_level}
								style={{ textTransform: 'capitalize' }}
								onChange={(e) => updatePet('energy_level', e.target.value)}
								disabled={!pet.active}
								variant="filled"
								label="Energy Level">
								{energyLevels.map((energyLevel) => (
									<MenuItem
										style={{ textTransform: 'capitalize' }}
										key={energyLevel}
										value={energyLevel}>
										{energyLevel}
									</MenuItem>
								))}
							</TextField>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
								Sensitive Body Parts
							</Typography>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option.name || ''}
								disableCloseOnSelect
								size="small"
								id="trait-tags"
								className={classes.autoComplete}
								options={tags ? tags.sensitivity : []}
								value={petSensitivityTags && petSensitivityTags.length ? petSensitivityTags : []}
								onChange={(e, v) => updateTags('sensitivity', v)}
								disabled={!pet.active}
								onClose={(e) => {
									e.target.value = ''
								}}
								renderTags={(selected) =>
									selected.map((value) => (
										<DeletableChip
											key={value._id}
											label={value.name}
											{...{
												onDelete: pet.active ? () => removeTag('sensitivity', value) : null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText
											style={{ textTransform: 'capitalize' }}
											primary={option.name}
										/>
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Select All Sensitive Body Parts"
									/>
								)}
							/>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
								Behavior Tags
							</Typography>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option.name || ''}
								disableCloseOnSelect
								size="small"
								id="trait-tags"
								className={classes.autoComplete}
								options={tags ? tags.behavior : []}
								value={petBehaviorTags && petBehaviorTags.length ? petBehaviorTags : []}
								onChange={(e, v) => updateTags('behavior', v)}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.map((value) => (
										<DeletableChip
											key={value._id}
											label={value.name}
											{...{
												onDelete: pet.active ? () => removeTag('behavior', value) : null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText
											style={{ textTransform: 'capitalize' }}
											primary={option.name}
										/>
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Select All Behavior Tags"
									/>
								)}
							/>
						</div>
					</div>
					<div className={classes.grid} style={{ gap: '.5rem' }}>
						<Typography variant="body2" style={{ fontWeight: 800 }}>
							Client Notes
						</Typography>
						<div
							style={{
								width: '100%',
								border: '1px solid #E5E5E5',
								borderRadius: 6,
								padding: 16,
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'flex-start'
							}}>
							{pet.notes > '' ? (
								<Typography
									variant="body2"
									style={{ fontWeight: 600, color: '#1A1A35', marginLeft: 8 }}>
									{pet.notes}
								</Typography>
							) : (
								<Typography
									variant="body2"
									style={{ fontWeight: 600, color: '#1A1A35', marginLeft: 8 }}>
									None
								</Typography>
							)}
						</div>
					</div>
					<div className={classes.grid} style={{ gap: '.5rem' }}>
						<div
							className={clsx([classes.fieldWrapper, 'field-wrapper'])}
							style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
							<Typography
								variant="body2"
								style={{
									fontWeight: 800,
									marginBottom: 0,
									display: 'flex',
									alignItems: 'center'
								}}>
								Dog Incompatibility
							</Typography>
							<Button
								variant="text"
								color="primary"
								className={classes.footerButton}
								onClick={() => openDogBeef(true)}>
								EDIT
							</Button>
						</div>
						<div
							style={{
								width: '100%',
								border: '1px solid #E5E5E5',
								borderRadius: 6,
								padding: 16,
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'flex-start'
							}}>
							<Typography variant="body2" style={{ fontWeight: 600, color: '#1A1A35' }}>
								Beef With
							</Typography>
							{pet.beefs?.length ? (
								pet.beefs.map((dog, i) => {
									const isLast = i === pet.beefs.length - 1
									return (
										<a
											href={`/clients/${dog.household_id}/pets/${dog._id}`}
											target="_blank"
											style={{
												textDecoration: 'none',
												marginLeft: '8px',
												color: purple[500],
												fontSize: '16px',
												fontWeight: '800'
											}}>
											{dog.full_name}
											{!isLast && ','}
										</a>
									)
								})
							) : (
								<Typography
									variant="body2"
									style={{ fontWeight: 600, color: '#1A1A35', marginLeft: 8 }}>
									None
								</Typography>
							)}
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
								Playgroups
							</Typography>
							<TextField
								select
								value={pet.best_fit_playgroup_type}
								onChange={(e) => updateBestFitPlaygroup(e.target.value)}
								style={{ marginBottom: 16, textTransform: 'capitalize', width: '100%' }}
								variant="filled"
								label="Best Fit Playgroup Type"
								disabled={!pet.active}>
								{getBestFits().map((type) => {
									return (
										<MenuItem key={type._id} value={type._id} label={type.name}>
											{type.name}
										</MenuItem>
									)
								})}
								<MenuItem value={null} label="None">
									None
								</MenuItem>
							</TextField>
							<div className={classes.rowThing}>
								<Checkbox
									checked={showOnlyFits}
									size="small"
									onClick={(e) => {
										setShowOnlyFits(!showOnlyFits)
									}}
								/>
								Show Only Playgroups That Fit Profile
							</div>

							<TextField
								select
								value={pet.alt_playgroup_type}
								onChange={(e) => updatePet('alt_playgroup_type', e.target.value)}
								style={{ marginBottom: 16, textTransform: 'capitalize', width: '100%' }}
								variant="filled"
								label="Alternate Playgroup Type"
								disabled={!pet.active}>
								{getAltFits().map((type) => {
									return (
										<MenuItem value={type._id} label={type.name}>
											{type.name}
										</MenuItem>
									)
								})}
								<MenuItem value={null} label="None">
									None
								</MenuItem>
							</TextField>

							<div className={classes.rowThing}>
								<Checkbox
									checked={showOnlyFitsAlt}
									size="small"
									onClick={(e) => {
										setShowOnlyFitsAlt(!showOnlyFitsAlt)
									}}
								/>
								Show Only Playgroups That Fit Profile
							</div>

							<Autocomplete
								multiple
								getOptionLabel={(option) => option.name || ''}
								disableCloseOnSelect
								size="small"
								id="playgroup-selects"
								className={classes.autoComplete}
								options={getOtherFits()}
								value={otherPlaygroupTypes || []}
								onChange={(e, v) => updateOtherPlaygroups(v)}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.filter(Boolean).map((value) => {
										return (
											<DeletableChip
												key={value._id}
												label={value.name}
												{...{
													onDelete: pet.active
														? () =>
																updateOtherPlaygroups(
																	otherPlaygroupTypes.filter(
																		(p) => p && p._id !== value._id
																	)
																)
														: null
												}}
											/>
										)
									})
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText
											style={{ textTransform: 'capitalize' }}
											primary={option.name}
										/>
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Flex Playgroup Types"
									/>
								)}
							/>

							<div className={classes.rowThing}>
								<Checkbox
									checked={showOnlyFitsOther}
									size="small"
									onClick={(e) => {
										setShowOnlyFitsOther(!showOnlyFitsOther)
									}}
								/>
								Show Only Playgroups That Fit Profile
							</div>
						</div>
					</div>
				</CardContent>
			</Collapse>
			<DogBeef
				open={dogBeef}
				setOpen={openDogBeef}
				onClose={() => openDogBeef(false)}
				onCancelled={() => openDogBeef(false)}
				pet={pet}
				setPet={setPet}
				updateBeefs={updateBeefs}
				addBeefs={addBeefs}
			/>
		</Card>
	)
}

export default PetBehaviorCard
